import { useState } from "react";

import { CreateAbsencePanel } from "~/modules/absence/components/AbsencesView/components/CreateAbsencePanel";
import type { useAbsenceActions } from "~/modules/absence/hooks/useAbsenceActions.tsx";
import { type CreateEntryTab, CreateEntryTabs } from "~/modules/dashboard/components/TimeTrackingCalendar/CreateEntryTabs.tsx";
import { CreateTimeTrackingPanel } from "~/modules/timeTracking/components/CreateTimeTrackingPanel";
import type { TimeTrackingController } from "~/modules/timeTracking/hooks/useTimeTrackingActions.ts";
import { noTrackingDayReason } from "~/modules/timeTracking/utils/noTrackingDayReason.ts";

export type CreateEntryContentProps = {
	controller: TimeTrackingController;
	/** `null` for a user without a staff member, and until the inputs of the absence form are loaded. */
	absencePanelInputs: ReturnType<typeof useAbsenceActions>["createPanelInputs"];
};

/** The tabs and the shown panel of the create sidebar. The tab state lives here, so a closed sidebar drops it. */
export function CreateEntryContent({ controller, absencePanelInputs }: CreateEntryContentProps) {
	const { queries } = controller;
	const trackingIsLocked = controller.selectedDate !== null && !controller.canAddTrackingOn(controller.selectedDate);
	const [tab, setTab] = useState<CreateEntryTab>(trackingIsLocked ? "absence" : "timeTracking");

	return (
		<>
			{absencePanelInputs && <CreateEntryTabs tab={tab}
													onChange={setTab}
													lockedTab={trackingIsLocked ? "timeTracking" : undefined} />}
			{absencePanelInputs && tab === "absence" ? (
				<CreateAbsencePanel {...absencePanelInputs}
									onClose={controller.closeCreate}
									presetDate={controller.selectedDate ?? undefined} />
			) : trackingIsLocked ? (
				<p className="whitespace-pre-line px-7 py-6 text-sm text-gray-600">{noTrackingDayReason}</p>
			) : (
				<CreateTimeTrackingPanel activeStaffings={controller.openActiveStaffings}
										 nonProjectIsClosed={controller.nonProjectIsClosed}
										 userDeliverables={queries.userDeliverables.data ?? []}
										 userLocations={queries.userLocations.data ?? []}
										 trackedStaffingIds={controller.trackedStaffingIdsOnSelectedDay}
										 onClose={controller.closeCreate}
										 parentIsVisible={controller.selectedDate !== null}
										 date={controller.selectedDate}
										 locations={queries.locations.data ?? []}
										 quotas={queries.activeStaffingsAndQuotas.data?.quotas ?? {}}
										 selectedUserId={controller.userId}
										 timeTrackingTypes={queries.timeTrackingTypes.data ?? []}
										 users={queries.users.data ?? []}
										 userIsFreelancer={controller.userIsFreelancer} />
			)}
		</>
	);
}
